import clsx from "clsx";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { StaticImageData } from "next/image";
import { ReactNode, useContext, useRef, useState } from "react";
import { FadeIn } from "~/components/animations";
import { ResponsiveImage } from "~/components/ResponsiveImage";
import { ViewportContext } from "~/providers/ViewportProvider";
import { Icons8checkmark } from "~/src/components/icons";

type ServiceHighlightProps = {
  title: string;
  description: ReactNode | ReactNode[];
  subTitle?: ReactNode;
  bullets: ReactNode[];
  note?: ReactNode;
  imageSrc: StaticImageData | string;
  imageAlt: string;
  imageSizes?: string;
  reverseOnMobile?: boolean;
  reverseOnDesktop?: boolean;
  stickyTop?: number;
};

type HighlightImageProps = {
  imageSrc: StaticImageData | string;
  imageAlt: string;
  imageSizes?: string;
  stickyTop: number;
};

const StaticHighlightImage = ({
  imageSrc,
  imageAlt,
  imageSizes,
}: HighlightImageProps) => (
  <div className="relative">
    <div className="relative overflow-hidden rounded-sm shadow-[0_18px_40px_-24px_rgba(15,23,42,0.45)]">
      <ResponsiveImage
        src={imageSrc}
        sizes={imageSizes}
        alt={imageAlt}
        placeholder="blur"
        imageClassName="scale-[1.03] transition-transform duration-700"
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-slate-950/10 via-transparent to-white/20" />
    </div>
  </div>
);

const DesktopHighlightImage = ({
  imageSrc,
  imageAlt,
  imageSizes,
  stickyTop,
}: HighlightImageProps) => {
  const imageRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: imageRef,
    offset: ["start end", "end start"],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], [24, -24]);

  return (
    <div
      ref={imageRef}
      className="relative top-0 md:sticky"
      style={{ top: `${stickyTop}px` }}
    >
      <motion.div
        className="relative overflow-hidden rounded-sm shadow-[0_18px_40px_-24px_rgba(15,23,42,0.45)]"
        style={{ y: imageY }}
        initial={{ scale: 1.06, opacity: 0.92, filter: "blur(10px)" }}
        whileInView={{ scale: 1, opacity: 1, filter: "blur(0px)" }}
        viewport={{ once: true, amount: 0.35 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      >
        <ResponsiveImage
          src={imageSrc}
          sizes={imageSizes}
          alt={imageAlt}
          placeholder="blur"
          imageClassName="scale-[1.03] transition-transform duration-700"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-slate-950/10 via-transparent to-white/20" />
      </motion.div>
    </div>
  );
};

const ServiceHighlight: React.FC<ServiceHighlightProps> = ({
  title,
  description,
  subTitle,
  bullets,
  note,
  imageSrc,
  imageAlt,
  imageSizes,
  reverseOnMobile = false,
  reverseOnDesktop = false,
  stickyTop = 100,
}) => {
  const { isMobile } = useContext(ViewportContext);
  const [isExpanded, setIsExpanded] = useState(false);
  const descriptionArray = Array.isArray(description) ? description : [description];
  const imageAnimationDirection = reverseOnDesktop && !isMobile ? "right" : "left";
  const textAnimationDirection = reverseOnDesktop && !isMobile ? "left" : "right";

  return (
    <FadeIn direction="up" duration={0.7}>
      <div
        className={clsx(
          "flex flex-col rounded bg-white shadow-sm md:grid md:grid-cols-2",
          isMobile ? "gap-3 p-3" : "gap-8 p-8",
          reverseOnMobile && isMobile && "flex-col-reverse"
        )}
      >
        <FadeIn
          direction={imageAnimationDirection}
          delay={0.2}
          duration={0.6}
          className={clsx(reverseOnDesktop && "md:order-2")}
        >
          {isMobile ? (
            <StaticHighlightImage
              imageSrc={imageSrc}
              imageAlt={imageAlt}
              imageSizes={imageSizes}
              stickyTop={stickyTop}
            />
          ) : (
            <DesktopHighlightImage
              imageSrc={imageSrc}
              imageAlt={imageAlt}
              imageSizes={imageSizes}
              stickyTop={stickyTop}
            />
          )}
        </FadeIn>

        <FadeIn
          direction={textAnimationDirection}
          delay={0.3}
          duration={0.6}
          className={clsx(reverseOnDesktop && "md:order-1")}
        >
          <div className="min-w-0 flex-1 text-gray-700">
            <h3 className="mb-4 text-xl font-bold h3">{title}</h3>

            {isMobile ? (
              <>
                <p className="mb-3 text-sm">{descriptionArray[0]}</p>

                <button
                  type="button"
                  onClick={() => setIsExpanded(!isExpanded)}
                  aria-expanded={isExpanded}
                  className="flex w-full items-center justify-between gap-2 rounded-lg bg-sky-50 px-4 py-3 text-sm font-semibold text-sky-800 transition hover:bg-sky-100"
                >
                  <span>{isExpanded ? "Pokaż mniej" : "Dowiedz się więcej"}</span>
                  <motion.svg
                    animate={{ rotate: isExpanded ? 180 : 0 }}
                    transition={{ duration: 0.25 }}
                    className="h-4 w-4 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </motion.svg>
                </button>

                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.div
                      key="accordion-content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="pt-4">
                        {descriptionArray.slice(1).map((paragraph, i) => (
                          <p key={i} className="mb-4 text-sm">
                            {paragraph}
                          </p>
                        ))}
                        {subTitle && (
                          <p className="mb-2 text-sm text-gray-800">{subTitle}</p>
                        )}
                        <ul className="mb-4 mt-2">
                          {bullets.map((check, i) => (
                            <li key={i} className="mb-3 flex min-h-[32px] items-center text-sm">
                              <Icons8checkmark className="-ml-1 mr-2 h-6 w-6 flex-shrink-0" />
                              {check}
                            </li>
                          ))}
                        </ul>
                        {note && <p className="mb-2 text-sm">{note}</p>}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            ) : (
              <>
                {descriptionArray.map((paragraph, i) => (
                  <p key={i} className="mb-4">
                    {paragraph}
                  </p>
                ))}
                {subTitle && <p className="mb-2 text-gray-800">{subTitle}</p>}
                <ul className="mb-4 mt-2 list-disc">
                  {bullets.map((check, i) => (
                    <li
                      key={i}
                      className="flex h-20 items-center"
                    >
                      <Icons8checkmark className="-ml-1 mr-2 h-7 w-7 flex-shrink-0" />
                      {check}
                    </li>
                  ))}
                </ul>
                {note && <p className="mb-2">{note}</p>}
              </>
            )}
          </div>
        </FadeIn>
      </div>
    </FadeIn>
  );
};

export default ServiceHighlight;
